import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { admin } from '../utils/api';
import toast from 'react-hot-toast';

import LocationManager from './LocationManager';

export default function AdminPanel() {

  const { t, user } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [mods, setMods] = useState([]);
  const [users, setUsers] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [tab, setTab] = useState('overview');


  const [acting, setActing] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [tokenAmount, setTokenAmount] = useState('');
  const [tokenReason, setTokenReason] = useState('');
  const [updatingTokens, setUpdatingTokens] = useState(false);
  const { fetchBalance } = useApp();



  useEffect(() => {
    if (!user || user.role !== 'SUPERADMIN') { navigate('/'); return; }
    Promise.all([admin.stats(), admin.moderators(), admin.users(), admin.getTraffic()])
      .then(([s, m, u, t]) => {
        setStats(s);
        setMods(m.moderators ?? []);
        setUsers(u.users ?? []);
        setTraffic(t);
      })
      .catch(() => toast.error(t('error_occurred')));

  }, [user, navigate, t]);

  const handleMod = async (action, id) => {
    setActing(id);
    try {
      if (action === 'approve') await admin.approveModerator(id);
      if (action === 'reject') await admin.rejectModerator(id);
      toast.success('Done');
      const m = await admin.moderators();
      setMods(m.moderators ?? []);
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setActing(null); }
  };


  const handleUserAction = async (action, id) => {
    if (action === 'delete') {
      const u = users.find(x => x.id === id);
      setDeletingUser(u || { id });
      return;
    }
    setActing(id);
    try {
      if (action === 'deactivate') await admin.deactivateUser(id);
      if (action === 'activate') await admin.activateUser(id);
      toast.success('User updated');
      const u = await admin.users();
      setUsers(u.users ?? []);
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setActing(null); }
  };

  const confirmDelete = async () => {
    if (!deletingUser) return;
    const id = deletingUser.id;
    setActing(id);
    try {
      await admin.deleteUser(id);
      toast.success('User deleted');
      const u = await admin.users();
      setUsers(u.users ?? []);
      setDeletingUser(null);
    } catch (err) { toast.error(err.error || t('error_occurred')); }
    finally { setActing(null); }
  };

  const handleToggleMaintenance = async () => {
    const current = stats?.configs?.find(c => c.config_key === 'maintenance_mode')?.config_value || 'off';
    const next = current === 'on' ? 'off' : 'on';
    setActing('maintenance');
    try {
      await admin.updateConfig({ configs: { maintenance_mode: next } });
      toast.success(`Maintenance mode ${next.toUpperCase()}`);
      const s = await admin.stats();
      setStats(s);
      setShowMaintenanceModal(false);
    } catch (err) { toast.error(t('error_occurred')); }
    finally { setActing(null); }
  };


  const handleViewUser = async (u) => {
    setViewingUser(u);
    setUserDetails(null);
    setTokenAmount('');
    setTokenReason('');
    try {
      const data = await admin.getUserDetails(u.id);
      setUserDetails(data);
    } catch (err) { toast.error('Failed to load user data'); setViewingUser(null); }
  };

  const handleUpdateTokens = async (e) => {
    e.preventDefault();
    if (!userDetails?.user?.id) return;
    setUpdatingTokens(true);
    try {
      await admin.updateTokens(userDetails.user.id, {
        amount: parseInt(tokenAmount),
        reason: tokenReason || 'Admin manual adjustment'
      });
      toast.success('Tokens updated!');
      // Refresh user details to show new balance & transaction
      const data = await admin.getUserDetails(userDetails.user.id);
      setUserDetails(data);
      // Update the user list too
      const uRes = await admin.users();
      setUsers(uRes.users ?? []);
      // IMPORTANT: Update self balance if the updated user is the admin
      if (userDetails.user.id === user.id) {
        fetchBalance();
      }
    } catch (err) {
      toast.error(err.error || 'Update failed');
    } finally {
      setUpdatingTokens(false);
    }
  };

  const isMaintenanceOn = stats?.configs?.find(c => c.config_key === 'maintenance_mode')?.config_value === 'on';

  const statRows = stats ? [
    ['Total users',          stats.totalUsers],
    ['Seekers',              stats.totalSeekers],
    ['Posters',              stats.totalPosters],
    ['Active jobs',          stats.activeJobs],
    ['Completed jobs',       stats.completedJobs],
    ['Active seek ads',      stats.activeSeekAds],
    ['Hired seekers',        stats.hiredSeekers],
    ['Tokens issued',        stats.tokensIssued],
    ['Pending token reqs',   stats.pendingTokenRequests],
    ['Pending hire reqs',    stats.pendingHireRequests],
    ['Pending moderators',   stats.pendingModerators],
  ] : [];

  const modStatusColor = { ACTIVE: 'chip-green', PENDING: 'chip-amber', REJECTED: 'chip-red', DISABLED: 'chip-gray' };

  const TABS = [
    ['overview', 'Overview', '📊'],
    ['moderators', 'Moderators', '🛡️'],
    ['users', 'Users', '👥'],
    ['locations', 'Locations', '📍'],
    ['traffic', 'Traffic', '📈']
  ];

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-menu">
          <div className="section-title hide-mobile" style={{ fontSize: '11px', marginBottom: '8px' }}>Admin Menu</div>
          {TABS.map(([k, label, icon]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`sidebar-link ${tab === k ? 'active' : ''}`}>
              <span style={{ fontSize: '16px' }}>{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>

        <div className="sidebar-footer">
          <button 
            className="sidebar-link" 
            style={{ padding: '8px 0', width: '100%', justifyContent: 'flex-start' }}
            disabled={acting === 'maintenance'}
            onClick={() => setShowMaintenanceModal(true)}
          >
            <span style={{ fontSize: '16px' }}>{isMaintenanceOn ? '🔴' : '🟢'}</span>
            <span>{isMaintenanceOn ? 'Maintenance' : 'System UP'}</span>
          </button>
          <button 
            className="sidebar-link" 
            style={{ padding: '8px 0', width: '100%', justifyContent: 'flex-start' }}
            onClick={() => navigate('/admin/config')}
          >
            <span style={{ fontSize: '16px' }}>⚙️</span>
            <span>{t('platform_config')}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content fade-in">
        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: 700 }}>{t('admin_panel')}</h1>
            <p style={{ color: 'var(--text3)', fontSize: '14px', marginTop: '4px' }}>Super Admin · <span style={{ color: 'var(--text)', fontWeight: 600 }}>{user?.fullName}</span></p>
          </div>
        </div>

        <div className="stagger">
          {/* Overview */}
          {tab === 'overview' && stats && (
            <div>
              <div className="section-title">Quick Stats</div>
              <div className="stat-grid" style={{ marginBottom: '32px' }}>
                {[
                  ['Total Users', stats.totalUsers],
                  ['Active Jobs', stats.activeJobs],
                  ['Seek Ads', stats.activeSeekAds],
                  ['Hired Seekers', stats.hiredSeekers],
                  ['Tokens Issued', stats.tokensIssued],
                  ['Pending Reqs', stats.pendingTokenRequests]
                ].map(([l, v]) => (
                  <div key={l} className="stat-card">
                    <div className="val">{v ?? '—'}</div>
                    <div className="lbl">{l}</div>
                  </div>
                ))}
              </div>

              <div className="card">
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Detailed Platform Analytics</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 32px' }}>
                  {statRows && statRows.map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: '14px' }}>
                      <span style={{ color: 'var(--text2)' }}>{label}</span>
                      <span style={{ fontWeight: 600 }}>{val ?? '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Moderators */}
          {tab === 'moderators' && (
            <div>
              <div className="section-title">Moderation Team</div>
              {mods.length === 0 ? <div className="empty-state">No moderators registered</div>
                : mods.map(mod => (
                  <div key={mod.id} className="card" style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--brand-light)', color: 'var(--brand-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                        {mod.full_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{mod.full_name}</div>
                        <div style={{ fontSize: '13px', color: 'var(--text3)' }}>{mod.email} · {mod.moderator_field ?? 'General'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span className={`chip ${modStatusColor[mod.moderator_status] ?? 'chip-gray'}`}>{mod.moderator_status}</span>
                      {mod.moderator_status === 'PENDING' && (
                        <>
                          <button className="btn btn-success btn-sm" disabled={acting === mod.id} onClick={() => handleMod('approve', mod.id)}>Approve</button>
                          <button className="btn btn-danger btn-sm" disabled={acting === mod.id} onClick={() => handleMod('reject', mod.id)}>Reject</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Users */}
          {tab === 'users' && (
            <div>
              <div className="section-title">Platform Users</div>
              {users.map(u => (
                <div key={u.id} className="card" style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px', flexShrink: 0 }}>
                      {u.full_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '15px' }}>{u.full_name}</div>
                      <div style={{ fontSize: '13px', color: 'var(--text3)' }}>{u.email} · {u.phone}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      <span className="chip chip-gray">{u.role}</span>
                      {u.moderator_status === 'DEACTIVATED' && <span className="chip chip-red">DEACTIVATED</span>}
                      <span style={{ fontSize: '13px', color: 'var(--text3)', display: 'flex', alignItems: 'center', marginLeft: '6px', fontWeight: 600 }}>🪙 {u.tokens}</span>
                    </div>

                    <div style={{ display: 'flex', gap: '6px' }}>
                      {u.moderator_status === 'DEACTIVATED' ? (
                        <button className="btn btn-outline btn-sm" disabled={acting === u.id} onClick={() => handleUserAction('activate', u.id)}>Activate</button>
                      ) : (
                        <button className="btn btn-outline btn-sm" style={{ color: 'var(--amber)' }} disabled={acting === u.id} onClick={() => handleUserAction('deactivate', u.id)}>Deactivate</button>
                      )}
                      <button className="btn btn-outline btn-sm" style={{ color: 'var(--brand)' }} onClick={() => handleViewUser(u)}>View</button>
                      <button className="btn btn-outline btn-sm" style={{ color: 'var(--red)' }} disabled={acting === u.id} onClick={() => handleUserAction('delete', u.id)}>✕</button>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--text3)', minWidth: '70px', textAlign: 'right' }}>{new Date(u.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Locations */}
          {tab === 'locations' && (
            <div>
              <div className="section-title">Geographic Hierarchy</div>
              <LocationManager />
            </div>
          )}

          {/* Traffic */}
          {tab === 'traffic' && traffic && (
            <div>
              <div className="section-title">Traffic Analytics</div>
              <div className="card" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 600 }}>Daily Visitors</div>
                  <select
                    className="form-input"
                    style={{ width: 'auto', padding: '6px 12px', height: 'auto' }}
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {[...new Set(Object.keys(traffic.visitors || {}).map(d => d.slice(0, 7)))].sort().reverse().map(m => (
                      <option key={m} value={m}>{new Date(m + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}</option>
                    ))}
                  </select>
                </div>

                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Unique IPs</th>
                        <th>Total Requests</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(traffic.visitors || {})
                        .filter(([date]) => date.startsWith(selectedMonth))
                        .reverse()
                        .map(([date, data]) => (
                          <tr key={date}>
                            <td>{date}</td>
                            <td>{data.unique}</td>
                            <td>{data.total}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card">
                <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Security Logs (Rate Limited)</div>
                {(!traffic.rate_limited || traffic.rate_limited.length === 0) ? (
                  <div className="empty-state">No rate limited IPs logged</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>IP Address</th>
                          <th>Time</th>
                          <th>Bucket</th>
                        </tr>
                      </thead>
                      <tbody>
                        {traffic.rate_limited.map((rl, i) => (
                          <tr key={i}>
                            <td style={{ fontFamily: 'monospace', color: 'var(--red-text)' }}>{rl.ip}</td>
                            <td>{rl.time}</td>
                            <td><span className="chip chip-amber">{rl.bucket}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Details Modal */}
      {viewingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setViewingUser(null)}>
          <div style={{ background: 'var(--surface)', width: '100%', maxWidth: '800px', maxHeight: '90vh', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column', animation: 'pop 0.3s ease' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: '18px' }}>User Context: {viewingUser.full_name}</div>
              <button className="btn btn-xs btn-outline" onClick={() => setViewingUser(null)}>✕ Close</button>
            </div>
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              {!userDetails ? <div className="page-loader"><div className="spinner" /></div> : (
                <div className="stagger">
                  {/* Basic Info */}
                  <div className="card" style={{ marginBottom: '20px' }}>
                    <div className="section-title">Identity & Access</div>
                    <div style={{ fontSize: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                      <div><span style={{ color: 'var(--text3)' }}>Role:</span> <span className="chip chip-gray">{userDetails.user.role}</span></div>
                      <div><span style={{ color: 'var(--text3)' }}>Email:</span> {userDetails.user.email}</div>
                      <div><span style={{ color: 'var(--text3)' }}>Phone:</span> {userDetails.user.phone}</div>
                      <div><span style={{ color: 'var(--text3)' }}>Joined:</span> {new Date(userDetails.user.createdAt).toLocaleString()}</div>
                      <div style={{ background: 'var(--brand-light)', padding: '10px', borderRadius: '8px', border: '1px solid var(--brand-dark)' }}>
                        <div style={{ fontWeight: 700, fontSize: '12px', color: 'var(--brand-dark)', textTransform: 'uppercase', marginBottom: '8px' }}>Adjust Wallet</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '18px' }}>🪙</span>
                          <span style={{ fontWeight: 700, fontSize: '20px' }}>{userDetails.user.tokens}</span>
                          <span style={{ fontSize: '12px', color: 'var(--text3)' }}>Tokens</span>
                        </div>
                        <form onSubmit={handleUpdateTokens} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                              className="form-input"
                              type="number"
                              placeholder="+/- Amount"
                              value={tokenAmount}
                              onChange={e => setTokenAmount(e.target.value)}
                              style={{ width: '100px', height: '32px', fontSize: '13px' }}
                              required
                            />
                            <input
                              className="form-input"
                              placeholder="Reason"
                              value={tokenReason}
                              onChange={e => setTokenReason(e.target.value)}
                              style={{ flex: 1, height: '32px', fontSize: '13px' }}
                              required
                            />
                            <button className="btn btn-primary btn-sm" type="submit" disabled={updatingTokens}>
                              {updatingTokens ? '...' : 'Update'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>

                  {/* Jobs */}
                  {userDetails.jobs.length > 0 && (
                    <div className="card" style={{ marginBottom: '20px' }}>
                      <div className="section-title">Posted Jobs ({userDetails.jobs.length})</div>
                      {userDetails.jobs.map(j => (
                        <div key={j.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: '14px' }}>
                          <div style={{ fontWeight: 600 }}>{j.title}</div>
                          <div style={{ color: 'var(--text3)', fontSize: '12px', marginTop: '4px' }}>{j.status} · {j.location} · ৳{Number(j.budget).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Transactions */}
                  {userDetails.transactions.length > 0 && (
                    <div className="card">
                      <div className="section-title">Token Ledger</div>
                      {userDetails.transactions.slice(0, 10).map(t => (
                        <div key={t.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{t.type}</div>
                            <div style={{ color: 'var(--text3)', fontSize: '12px' }}>{t.note}</div>
                          </div>
                          <div style={{ fontWeight: 700, color: t.amount > 0 ? 'var(--brand)' : 'var(--red)' }}>
                            {t.amount > 0 ? '+' : ''}{t.amount}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Confirmation Modal */}
      {showMaintenanceModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setShowMaintenanceModal(false)}>
          <div style={{ background: 'var(--surface)', width: '100%', maxWidth: '400px', borderRadius: '16px', overflow: 'hidden', animation: 'pop 0.3s ease-out' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>{isMaintenanceOn ? '🟢' : '⚠️'}</div>
              <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '12px' }}>
                {isMaintenanceOn ? 'Restore System?' : 'Activate Maintenance?'}
              </h2>
              <p style={{ color: 'var(--text3)', fontSize: '14px', lineHeight: '1.6', marginBottom: '32px' }}>
                {isMaintenanceOn
                  ? 'All users will regain access to the platform services immediately.'
                  : 'Platform access will be restricted to Superadmins only. A maintenance notice will be displayed to all other users.'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  className={`btn btn-lg ${isMaintenanceOn ? 'btn-primary' : 'btn-danger'}`}
                  style={{ width: '100%' }}
                  disabled={acting === 'maintenance'}
                  onClick={handleToggleMaintenance}
                >
                  {acting === 'maintenance' ? 'Processing...' : (isMaintenanceOn ? 'Go Online Now' : 'Enter Maintenance Mode')}
                </button>
                <button className="btn btn-outline btn-lg" style={{ width: '100%' }} onClick={() => setShowMaintenanceModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={() => setDeletingUser(null)}>
          <div style={{ background: 'var(--surface)', width: '100%', maxWidth: '400px', borderRadius: '16px', overflow: 'hidden', animation: 'pop 0.3s ease-out' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>🗑️</div>
              <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '12px', color: 'var(--red)' }}>
                Delete User?
              </h2>
              <p style={{ color: 'var(--text3)', fontSize: '14px', lineHeight: '1.6', marginBottom: '32px' }}>
                Are you sure you want to delete <span style={{ color: 'var(--text)', fontWeight: 600 }}>{deletingUser.fullName || deletingUser.full_name || 'this user'}</span>? 
                This action is <strong>permanent</strong> and cannot be undone. All their data will be removed.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  className="btn btn-lg btn-danger"
                  style={{ width: '100%' }}
                  disabled={acting === deletingUser.id}
                  onClick={confirmDelete}
                >
                  {acting === deletingUser.id ? 'Deleting...' : 'Permanently Delete'}
                </button>
                <button className="btn btn-outline btn-lg" style={{ width: '100%' }} onClick={() => setDeletingUser(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
